import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { PartnerService } from '@/services';
import { Partner, QueryParams } from '@/model';
import { CreatePartnerPayload } from '../request/partner';
import { PartnerConverter } from '@/converters/partner';
import { GetParamsObject } from '@/model/query-params';

export const usePartnersAll = (params?: QueryParams) => {
  return useQuery({
    queryKey: ['GET_PARTNERS', params],
    queryFn: async (): Promise<Partner[]> => {
      let all: Partner[] = [];
      let currentPage = 1;
      let totalPages = 1;
      do {
        const [err, data] = await PartnerService.getPartners(params);
        if (err) throw err;
        all = [...all, ...PartnerConverter.toDomainFromGetResponse(data)];
        totalPages = data.meta.pages;
        currentPage++;
      } while (currentPage <= totalPages);
      return all;
    },
  });
};

export const usePartners = (params?: QueryParams) => {
  const query = useQuery(
    ['GET_PARTNERS', params],
    async () => {
      const [err, data] = await PartnerService.getPartners(params);
      if (err) {
        throw err;
      }
      return {
        data: PartnerConverter.toDomainFromGetResponse(data),
        meta: data.meta,
      };
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);

  return returnQuery;
};

export const useAllPartners = (isEnable: boolean) => {
  const query = useQuery(
    ['GET_PARTNERS'],
    async (): Promise<Partner[]> => {
      let all: Partner[] = [];
      let currentPage = 1;
      let totalPages = 1;
      do {
        const [err, data] = await PartnerService.getPartners(GetParamsObject({}, 1, 900, 'asc', 'name'));
        if (err) throw err;
        all = [...all, ...PartnerConverter.toDomainFromGetResponse(data)];
        totalPages = data.meta.pages;
        currentPage++;
      } while (currentPage <= totalPages);
      return all;
    },
    {
      refetchOnWindowFocus: false,
      enabled: isEnable,
      suspense: !isEnable,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);
  return returnQuery;
};

export const usePartnerById = (id: string) => {
  const query = useQuery(
    ['GET_PARTNER_BY_ID', id],
    async () => {
      const [err, data] = await PartnerService.getPartnerById(id);
      if (err) {
        throw err;
      }
      return PartnerConverter.toDomainFromByIdResponse(data);
    },
    {
      refetchOnWindowFocus: false,
      enabled: !!id,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);

  return returnQuery;
};

export const usePutPartnerMutation = () => {
  return useMutation({
    mutationKey: ['PUT_PARTNERS'],
    mutationFn: async ({ id, payload }: any) => {
      const [err, data] = await PartnerService.putPartner(id, payload);
      if (err) {
        throw err;
      }
      return PartnerConverter.toDomainFromPutResponse(data);
    },
  });
};

export const useDeletePartnerByIdMutation = () => {
  return useMutation({
    mutationKey: ['DELETE_PARTNER_BY_ID'],
    mutationFn: async (id: string) => {
      const [err, data] = await PartnerService.deletePartnerById(id);
      if (err) {
        throw err;
      }
      return data;
    },
  });
};

export const useGetPartnerByUUID = () => {
  return useMutation({
    mutationFn: async (uuid: string) => {
      const [err, data] = await PartnerService.getPartnerById(uuid);
      if (err) {
        throw err;
      }
      return PartnerConverter.toDomainFromByIdResponse(data);
    },
  });
};

export const useCreatePartnerMutation = () => {
  return useMutation({
    mutationKey: ['CREATE_PARTNER'],
    mutationFn: async (payload: CreatePartnerPayload) => {
      const [err, data] = await PartnerService.createPartner(payload);
      if (err) {
        throw err;
      }
      return data;
    },
  });
};
